import secureStorage from "../utils/secureStorage";
/**
 * useBiometricAuth - Web Authentication API Hook for Biometric Login
 * Supports Touch ID, Face ID, and Android Fingerprint/Face Recognition
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { axiosInstance } from '../App';

// Timeout constants
const BIOMETRIC_TIMEOUT_MS = 30000; // 30 seconds (reduced from 60s)
const ANDROID_TIMEOUT_MS = 25000; // 25 seconds for Android (slightly shorter)

// Check if WebAuthn is supported
const isWebAuthnSupported = () => {
  return window.PublicKeyCredential !== undefined && 
         typeof window.PublicKeyCredential === 'function';
};

// Check Chrome version on Android (WebAuthn requires Chrome 70+)
const getAndroidChromeVersion = () => {
  const match = navigator.userAgent.match(/Chrome\/(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
};

// Check if platform authenticator (Touch ID, Face ID, Fingerprint) is available
const isPlatformAuthenticatorAvailable = async () => {
  if (!isWebAuthnSupported()) {
    console.log('[BIOMETRIC] WebAuthn not supported');
    return false;
  }
  
  // Check Chrome version on Android
  if (isAndroidDevice()) {
    const chromeVersion = getAndroidChromeVersion();
    console.log('[BIOMETRIC] Android Chrome version:', chromeVersion);
    if (chromeVersion < 70) {
      console.log('[BIOMETRIC] Chrome version too old for WebAuthn');
      return false;
    }
  }
  
  try {
    const result = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    console.log('[BIOMETRIC] Platform authenticator available:', result);
    return result;
  } catch (error) {
    console.error('[BIOMETRIC] Error checking platform authenticator:', error);
    return false;
  }
};

// Check if the current context is secure (HTTPS or localhost)
const isSecureContext = () => {
  const isSecure = window.isSecureContext;
  const isLocalhost = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1';
  console.log('[BIOMETRIC] Secure context:', isSecure, 'Localhost:', isLocalhost);
  return isSecure || isLocalhost;
};

// Detect if device is Android
const isAndroidDevice = () => {
  return /android/i.test(navigator.userAgent);
};

// Detect if device is iOS
const isIOSDevice = () => {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
};

// Convert base64url to ArrayBuffer
const base64urlToBuffer = (base64url) => {
  const padding = '='.repeat((4 - base64url.length % 4) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer;
};

// Convert ArrayBuffer to base64url
const bufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = window.btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

export function useBiometricAuth() {
  const [isSupported, setIsSupported] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [biometricType, setBiometricType] = useState('fingerprint'); // 'fingerprint', 'face', 'biometric'
  const [isAndroid, setIsAndroid] = useState(false);
  const [selectedAndroidBiometric, setSelectedAndroidBiometric] = useState(null); // 'fingerprint' or 'face'
  const [registrationStatus, setRegistrationStatus] = useState('idle'); // 'idle', 'requesting', 'waiting_user', 'processing', 'error'
  const [statusMessage, setStatusMessage] = useState('');
  const abortControllerRef = useRef(null);

  // Check support on mount
  useEffect(() => {
    const checkSupport = async () => {
      // First check if context is secure
      if (!isSecureContext()) {
        console.warn('[BIOMETRIC] Not in secure context, biometric disabled');
        setIsSupported(false);
        setIsLoading(false);
        return;
      }

      const supported = await isPlatformAuthenticatorAvailable();
      setIsSupported(supported);
      
      const androidDevice = isAndroidDevice();
      setIsAndroid(androidDevice);
      
      console.log('[BIOMETRIC] Device info - Android:', androidDevice, 'Supported:', supported);
      
      // Detect biometric type based on platform
      if (supported) {
        if (isIOSDevice()) {
          // iOS devices - could be Touch ID or Face ID
          setBiometricType('face'); // Modern iPhones mostly use Face ID
        } else if (androidDevice) {
          // Check if user has previously selected a preference
          const savedPreference = localStorage.getItem('androidBiometricPreference');
          if (savedPreference === 'fingerprint' || savedPreference === 'face') {
            setSelectedAndroidBiometric(savedPreference);
            setBiometricType(savedPreference);
          } else {
            // Android devices can have fingerprint, face unlock, or both
            // Will show selection dialog when enabling
            setBiometricType('biometric');
          }
        } else if (/mac/i.test(navigator.userAgent)) {
          setBiometricType('fingerprint'); // Touch ID on Mac
        } else {
          setBiometricType('fingerprint'); // Default to fingerprint
        }
      }
      
      // Check if biometric is enabled for this device
      const biometricEnabled = secureStorage.getItem('biometricEnabled') === 'true';
      const storedCredentialId = secureStorage.getItem('biometricCredentialId');
      setIsEnabled(biometricEnabled && !!storedCredentialId);
      
      setIsLoading(false);
    };
    
    checkSupport();
  }, []);

  // Set Android biometric preference
  const setAndroidBiometricPreference = useCallback((type) => {
    if (type === 'fingerprint' || type === 'face') {
      localStorage.setItem('androidBiometricPreference', type);
      setSelectedAndroidBiometric(type);
      setBiometricType(type);
    }
  }, []);

  // Check if Android needs biometric selection
  const needsAndroidBiometricSelection = useCallback(() => {
    return isAndroid && isSupported && !selectedAndroidBiometric;
  }, [isAndroid, isSupported, selectedAndroidBiometric]);

  // Register biometric credential
  const registerBiometric = useCallback(async (userId, userName) => {
    console.log('[BIOMETRIC] Starting registration for user:', userId);
    
    if (!isSupported) {
      throw new Error('Biometric tidak disokong pada peranti ini');
    }

    // Check secure context again
    if (!isSecureContext()) {
      throw new Error('Biometric memerlukan sambungan HTTPS yang selamat');
    }

    // Cancel any existing registration attempt
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setRegistrationStatus('requesting');
    setStatusMessage('Menghubungi pelayan...');
    
    // Use shorter timeout for Android
    const timeoutMs = isAndroid ? ANDROID_TIMEOUT_MS : BIOMETRIC_TIMEOUT_MS;
    console.log('[BIOMETRIC] Using timeout:', timeoutMs, 'ms, isAndroid:', isAndroid);
    
    try {
      // Get challenge from server - send current origin for RP ID
      console.log('[BIOMETRIC] Requesting challenge from server...');
      const challengeResponse = await axiosInstance.post('/auth/biometric/register/challenge', {
        user_id: userId,
        origin: window.location.origin
      });
      
      const { challenge, rp_id, rp_name, user_id_bytes } = challengeResponse.data;
      console.log('[BIOMETRIC] Challenge received, RP ID:', rp_id);

      setRegistrationStatus('waiting_user');
      setStatusMessage(isAndroid 
        ? 'Sila sentuh sensor cap jari atau tunjukkan wajah anda...' 
        : 'Sila gunakan Touch ID atau Face ID...');

      // Create credential options with reduced timeout
      // For Android, use more lenient settings to improve compatibility
      const authenticatorSelection = isAndroid ? {
        // Android: Don't force 'platform' - let browser decide
        userVerification: 'preferred', // 'preferred' instead of 'required' for better compatibility
        residentKey: 'discouraged' // simpler credential type for Android
      } : {
        authenticatorAttachment: 'platform', // iOS/Mac: Use platform authenticator
        userVerification: 'required',
        residentKey: 'preferred'
      };

      const publicKeyCredentialCreationOptions = {
        challenge: base64urlToBuffer(challenge),
        rp: {
          name: rp_name || 'Taska Ummu Qaseh',
          id: rp_id || window.location.hostname
        },
        user: {
          id: base64urlToBuffer(user_id_bytes),
          name: userName || 'User',
          displayName: userName || 'User'
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' },   // ES256
          { alg: -257, type: 'public-key' }  // RS256
        ],
        authenticatorSelection,
        timeout: timeoutMs,
        attestation: 'none'
      };

      console.log('[BIOMETRIC] Calling navigator.credentials.create() with options:', JSON.stringify(authenticatorSelection));
      
      // Create a timeout promise for Android devices
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          console.log('[BIOMETRIC] Timeout reached after', timeoutMs + 2000, 'ms');
          reject(new Error('TIMEOUT'));
        }, timeoutMs + 2000); // Add 2 seconds buffer
      });

      // Create credential with race against timeout
      const credential = await Promise.race([
        navigator.credentials.create({
          publicKey: publicKeyCredentialCreationOptions
        }),
        timeoutPromise
      ]);

      console.log('[BIOMETRIC] Credential created successfully');

      if (!credential) {
        throw new Error('Gagal mencipta credential biometric');
      }

      setRegistrationStatus('processing');
      setStatusMessage('Menyimpan maklumat biometric...');

      // Send credential to server for storage
      const credentialData = {
        id: credential.id,
        raw_id: bufferToBase64url(credential.rawId),
        response: {
          client_data_json: bufferToBase64url(credential.response.clientDataJSON),
          attestation_object: bufferToBase64url(credential.response.attestationObject)
        },
        type: credential.type
      };

      await axiosInstance.post('/auth/biometric/register/complete', {
        user_id: userId,
        credential: credentialData
      });

      // Store credential ID locally
      secureStorage.setItem('biometricEnabled', 'true');
      secureStorage.setItem('biometricCredentialId', credential.id);
      secureStorage.setItem('biometricUserId', userId);
      
      setIsEnabled(true);
      setRegistrationStatus('idle');
      setStatusMessage('');
      return true;
    } catch (error) {
      console.error('[BIOMETRIC] Registration error:', error.name, error.message);
      setRegistrationStatus('error');
      
      // Handle specific errors with more user-friendly messages
      if (error.message === 'TIMEOUT') {
        setStatusMessage('Masa tamat. Dialog biometric tidak muncul.');
        console.log('[BIOMETRIC] Timeout - dialog may not have appeared');
        throw new Error('Masa tamat - sila pastikan peranti anda menyokong biometric dan cuba lagi');
      } else if (error.name === 'NotAllowedError') {
        setStatusMessage('Pendaftaran dibatalkan atau ditolak');
        console.log('[BIOMETRIC] NotAllowedError - user cancelled or denied');
        throw new Error('Pendaftaran biometric dibatalkan atau ditolak');
      } else if (error.name === 'SecurityError') {
        setStatusMessage('Ralat keselamatan');
        console.log('[BIOMETRIC] SecurityError - HTTPS required');
        throw new Error('Keselamatan: Pastikan laman web menggunakan HTTPS');
      } else if (error.name === 'InvalidStateError') {
        setStatusMessage('Credential sudah wujud');
        throw new Error('Credential sudah wujud untuk peranti ini');
      } else if (error.name === 'AbortError') {
        setStatusMessage('Proses dibatalkan');
        throw new Error('Proses pendaftaran dibatalkan');
      } else if (error.name === 'NotSupportedError') {
        setStatusMessage('Peranti tidak menyokong');
        console.log('[BIOMETRIC] NotSupportedError - platform authenticator not available');
        throw new Error('Peranti anda tidak menyokong pendaftaran biometric');
      } else if (error.name === 'UnknownError' || error.name === 'OperationError') {
        // Android-specific error when biometric is not set up
        setStatusMessage('Biometric tidak dikonfigurasi');
        console.log('[BIOMETRIC] UnknownError/OperationError - biometric may not be set up on device');
        throw new Error('Sila pastikan cap jari atau Face ID telah didaftarkan dalam Tetapan telefon anda');
      }
      
      // Generic Android-specific error message
      if (isAndroid) {
        setStatusMessage('Gagal pada peranti Android');
        throw new Error('Biometric tidak dapat diaktifkan pada peranti ini. Sila pastikan cap jari/Face ID telah didaftarkan dalam tetapan telefon.');
      }
      
      throw error;
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [isSupported, isAndroid]);

  // Authenticate using biometric
  const authenticateWithBiometric = useCallback(async () => {
    if (!isSupported || !isEnabled) {
      throw new Error('Biometric tidak aktif');
    }

    const credentialId = secureStorage.getItem('biometricCredentialId');
    const userId = secureStorage.getItem('biometricUserId');
    
    if (!credentialId || !userId) {
      throw new Error('Tiada credential biometric tersimpan');
    }

    // Use shorter timeout for Android
    const timeoutMs = isAndroid ? ANDROID_TIMEOUT_MS : BIOMETRIC_TIMEOUT_MS;

    setIsLoading(true);
    setRegistrationStatus('requesting');
    setStatusMessage('Menghubungi pelayan...');
    
    try {
      // Get challenge from server - send current origin for RP ID
      const challengeResponse = await axiosInstance.post('/auth/biometric/authenticate/challenge', {
        user_id: userId,
        origin: window.location.origin
      });
      
      const { challenge, rp_id, allowed_credentials } = challengeResponse.data;

      setRegistrationStatus('waiting_user');
      setStatusMessage(isAndroid 
        ? 'Sila sentuh sensor cap jari atau tunjukkan wajah anda...' 
        : 'Sila gunakan Touch ID atau Face ID...');

      // Create assertion options - use 'preferred' for Android for better compatibility
      const publicKeyCredentialRequestOptions = {
        challenge: base64urlToBuffer(challenge),
        rpId: rp_id || window.location.hostname,
        allowCredentials: allowed_credentials?.map(cred => ({
          id: base64urlToBuffer(cred.id),
          type: 'public-key',
          transports: isAndroid ? ['internal', 'hybrid'] : ['internal'] // Android: allow hybrid transport
        })) || [{
          id: base64urlToBuffer(credentialId),
          type: 'public-key',
          transports: isAndroid ? ['internal', 'hybrid'] : ['internal']
        }],
        userVerification: isAndroid ? 'preferred' : 'required', // Android: use 'preferred'
        timeout: timeoutMs
      };

      console.log('[BIOMETRIC] Calling navigator.credentials.get()...');

      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('TIMEOUT'));
        }, timeoutMs + 2000);
      });

      // Get assertion with race against timeout
      const assertion = await Promise.race([
        navigator.credentials.get({
          publicKey: publicKeyCredentialRequestOptions
        }),
        timeoutPromise
      ]);

      if (!assertion) {
        throw new Error('Pengesahan biometric gagal');
      }

      setRegistrationStatus('processing');
      setStatusMessage('Mengesahkan...');

      // Send assertion to server for verification
      const assertionData = {
        id: assertion.id,
        raw_id: bufferToBase64url(assertion.rawId),
        response: {
          client_data_json: bufferToBase64url(assertion.response.clientDataJSON),
          authenticator_data: bufferToBase64url(assertion.response.authenticatorData),
          signature: bufferToBase64url(assertion.response.signature),
          user_handle: assertion.response.userHandle ? bufferToBase64url(assertion.response.userHandle) : null
        },
        type: assertion.type
      };

      const verifyResponse = await axiosInstance.post('/auth/biometric/authenticate/complete', {
        user_id: userId,
        assertion: assertionData
      });

      setRegistrationStatus('idle');
      setStatusMessage('');
      return verifyResponse.data; // { user, token }
    } catch (error) {
      console.error('Biometric authentication error:', error);
      setRegistrationStatus('error');
      
      if (error.message === 'TIMEOUT') {
        setStatusMessage('Masa tamat');
        throw new Error('Masa tamat - sila cuba lagi');
      } else if (error.name === 'NotAllowedError') {
        setStatusMessage('Pengesahan dibatalkan');
        throw new Error('Pengesahan biometric dibatalkan atau ditolak');
      } else if (error.name === 'SecurityError') {
        setStatusMessage('Ralat keselamatan');
        throw new Error('Ralat keselamatan semasa pengesahan');
      }
      
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, isEnabled, isAndroid]);

  // Disable biometric
  const disableBiometric = useCallback(async () => {
    const userId = secureStorage.getItem('biometricUserId');
    
    try {
      if (userId) {
        await axiosInstance.post('/auth/biometric/disable', { user_id: userId });
      }
    } catch (error) {
      console.error('Error disabling biometric on server:', error);
    }
    
    secureStorage.removeItem('biometricEnabled');
    secureStorage.removeItem('biometricCredentialId');
    secureStorage.removeItem('biometricUserId');
    localStorage.removeItem('androidBiometricPreference');
    setIsEnabled(false);
    setSelectedAndroidBiometric(null);
    setRegistrationStatus('idle');
    setStatusMessage('');
    if (isAndroid) {
      setBiometricType('biometric');
    }
  }, [isAndroid]);

  // Cancel ongoing registration
  const cancelRegistration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsLoading(false);
    setRegistrationStatus('idle');
    setStatusMessage('');
  }, []);

  // Reset status
  const resetStatus = useCallback(() => {
    setRegistrationStatus('idle');
    setStatusMessage('');
  }, []);

  // Check if biometric login is available for quick login
  const canUseBiometricLogin = useCallback(() => {
    return isSupported && isEnabled && 
           secureStorage.getItem('biometricCredentialId') && 
           secureStorage.getItem('biometricUserId');
  }, [isSupported, isEnabled]);

  return {
    isSupported,
    isEnabled,
    isLoading,
    biometricType,
    isAndroid,
    selectedAndroidBiometric,
    registrationStatus,
    statusMessage,
    setAndroidBiometricPreference,
    needsAndroidBiometricSelection,
    registerBiometric,
    authenticateWithBiometric,
    disableBiometric,
    cancelRegistration,
    resetStatus,
    canUseBiometricLogin
  };
}

export default useBiometricAuth;
